import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

import type { AdjustmentBatchRow } from '@/services/inventoryAdjustmentService'
import {
  discountDateInputToApiIso,
  discountService,
  extractDiscountMasterIdFromResponse,
  type DiscountSavePayload,
} from '@/services/discountService'
import { useAuthStore } from '@/store/auth.store'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { DiscountItemLine } from '../types'
import { newDiscountItemLine } from '../types'
import { nextDiscountCode } from '../storage'

const SELECT_EMPTY = '__discount_empty__'
const SELECT_ANY = '0'

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

/** Allow typing decimals; avoid type="number" (spinners / some browsers block normal typing in table layouts). */
function sanitizeDiscountPercentRaw(raw: string): string {
  const cleaned = raw.replace(/[^\d.]/g, '')
  const firstDot = cleaned.indexOf('.')
  if (firstDot === -1) return cleaned
  return cleaned.slice(0, firstDot + 1) + cleaned.slice(firstDot + 1).replace(/\./g, '')
}

export const InventoryDiscountsEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const { user } = useAuthStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)
  const prevBranchIdRef = useRef(0)

  const recordId = id && id !== 'new' ? id : ''
  const isNew = !recordId

  const [code, setCode] = useState('')
  const [docDate, setDocDate] = useState(todayIso())
  const [startDate, setStartDate] = useState(todayIso())
  const [endDate, setEndDate] = useState(todayIso())

  const [itemOptions, setItemOptions] = useState<{ value: string; label: string }[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [saveLoading, setSaveLoading] = useState(false)
  const [detailLoading, setDetailLoading] = useState(false)
  /** Server master id: 0 = POST Save, &gt; 0 = PUT Update */
  const [serverDiscountId, setServerDiscountId] = useState(0)

  const [lines, setLines] = useState<DiscountItemLine[]>(() =>
    Array.from({ length: 3 }, () => newDiscountItemLine()),
  )

  const [batchesByLine, setBatchesByLine] = useState<Record<string, AdjustmentBatchRow[]>>({})
  const [batchesLoading, setBatchesLoading] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (isNew || !recordId) return
    const masterId = Number(recordId)
    if (!Number.isFinite(masterId) || masterId <= 0) {
      toast.error(t('inventoryDiscounts.notFound'))
      navigate('/inventory/schemes-discounts/discounts')
      return
    }
    if (!selectedBranchId) return

    let cancelled = false
    setDetailLoading(true)
    void Promise.all([
      discountService.getById(selectedBranchId, masterId),
      discountService.getLinesByMasterId(selectedBranchId, masterId),
    ])
      .then(([master, lineRows]) => {
        if (cancelled) return
        if (master.id <= 0) {
          toast.error(t('inventoryDiscounts.notFound'))
          navigate('/inventory/schemes-discounts/discounts')
          return
        }
        setServerDiscountId(master.id)
        setCode(master.code)
        setDocDate(master.date || todayIso())
        setStartDate(master.schemeStartDate || todayIso())
        setEndDate(master.schemeEndDate || todayIso())
        setLines(
          lineRows.length > 0 ? lineRows : Array.from({ length: 3 }, () => newDiscountItemLine()),
        )
        setBatchesByLine({})
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || t('inventoryDiscounts.detailLoadFailed'))
        navigate('/inventory/schemes-discounts/discounts')
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [isNew, recordId, selectedBranchId, navigate, t])

  useEffect(() => {
    if (isNew && !code) {
      setCode(nextDiscountCode())
    }
  }, [isNew, code])

  useEffect(() => {
    if (!selectedBranchId) {
      setItemOptions([])
      setBatchesByLine({})
      setBatchesLoading({})
      return
    }

    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setBatchesByLine({})
      setBatchesLoading({})
      setServerDiscountId(0)
      setLines((prev) =>
        prev.map((l) => ({
          ...l,
          itemId: '',
          batchId: '',
          serverLineId: undefined,
        })),
      )
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setItemsLoading(true)
    void discountService
      .getAvailableItems(selectedBranchId)
      .then((opts) => {
        if (!cancelled) setItemOptions(opts)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setItemOptions([])
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('unsellableInventory.itemsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  const loadBatches = (lineId: string, branchId: number, itemIdNum: number) => {
    setBatchesLoading((prev) => ({ ...prev, [lineId]: true }))
    void discountService
      .getAvailableBatches(branchId, itemIdNum)
      .then((rows) => {
        setBatchesByLine((prev) => ({ ...prev, [lineId]: rows }))
      })
      .catch(() => {
        toast.error(t('unsellableInventory.batchesLoadFailed'))
        setBatchesByLine((prev) => ({ ...prev, [lineId]: [] }))
      })
      .finally(() => {
        setBatchesLoading((prev) => ({ ...prev, [lineId]: false }))
      })
  }

  const linesBatchKey = useMemo(
    () => lines.map((l) => `${l.id}|${l.itemId}`).join(';;'),
    [lines],
  )

  useEffect(() => {
    if (!selectedBranchId) return
    for (const line of lines) {
      const itemNum = Number(line.itemId)
      if (itemNum) {
        const existing = batchesByLine[line.id]
        if (!existing || existing.length === 0) {
          loadBatches(line.id, selectedBranchId, itemNum)
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- hydrate batches when line items / branch change
  }, [linesBatchKey, selectedBranchId])

  const handleItemChange = (index: number, value: string) => {
    let lineId = ''
    setLines((prev) => {
      const next = [...prev]
      lineId = next[index].id
      if (value === SELECT_EMPTY) {
        next[index] = {
          ...next[index],
          itemId: '',
          batchId: '',
        }
      } else {
        next[index] = {
          ...next[index],
          itemId: value,
          batchId: SELECT_ANY,
        }
      }
      return next
    })
    setBatchesByLine((prev) => ({ ...prev, [lineId]: [] }))
    if (value === SELECT_EMPTY) return
    const itemNum = Number(value)
    if (selectedBranchId && itemNum) loadBatches(lineId, selectedBranchId, itemNum)
  }

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => newDiscountItemLine())])
  }

  const removeLine = (index: number) => {
    setLines((prev) => {
      if (prev.length <= 1) return prev
      const removed = prev[index]
      const next = prev.filter((_, i) => i !== index)
      setBatchesByLine((b) => {
        const { [removed.id]: _, ...rest } = b
        return rest
      })
      return next
    })
  }

  const handleBack = () => navigate('/inventory/schemes-discounts/discounts')

  const handleSave = async () => {
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.saveNoBranch'))
      return
    }

    const filled = lines.filter((l) => {
      const pct = Number(l.discountPercent)
      return (
        l.itemId &&
        l.batchId !== '' &&
        l.discountPercent.trim() !== '' &&
        Number.isFinite(pct) &&
        pct >= 0
      )
    })
    if (filled.length === 0) {
      toast.error(t('inventoryDiscounts.validationLines'))
      return
    }

    const masterIdForPayload = serverDiscountId
    const itemList = filled.map((l) => ({
      id: l.serverLineId != null && l.serverLineId > 0 ? l.serverLineId : 0,
      discountId: masterIdForPayload,
      itemId: Number(l.itemId),
      batchId: Number(l.batchId),
      discountPercent: Number(l.discountPercent) || 0,
      isDeleted: 'false',
    }))

    const payload: DiscountSavePayload = {
      id: masterIdForPayload,
      code: (code.trim() || nextDiscountCode()).trim(),
      date: discountDateInputToApiIso(docDate),
      schemeStartDate: discountDateInputToApiIso(startDate),
      schemeEndDate: discountDateInputToApiIso(endDate),
      branchId: selectedBranchId,
      itemList,
    }

    console.log('[Discounts] Save/Update request body (object):', payload)
    console.log('[Discounts] Save/Update request body (JSON):', JSON.stringify(payload, null, 2))

    setSaveLoading(true)
    try {
      const res =
        masterIdForPayload > 0
          ? await discountService.update(payload)
          : await discountService.save(payload)

      console.log('[Discounts] API response body (object):', res.data)
      console.log('[Discounts] API response body (JSON):', JSON.stringify(res.data, null, 2))

      const nextMasterId = extractDiscountMasterIdFromResponse(res.data) || masterIdForPayload
      if (nextMasterId > 0) setServerDiscountId(nextMasterId)

      await Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('inventoryDiscounts.saveSuccess'),
        confirmButtonText: t('common.ok'),
      })
      navigate('/inventory/schemes-discounts/discounts')
    } catch (err: unknown) {
      let msg = ''
      if (err && typeof err === 'object' && 'response' in err) {
        const data = (err as { response?: { data?: unknown } }).response?.data
        if (typeof data === 'string') msg = data
        else if (data && typeof data === 'object') {
          const d = data as Record<string, unknown>
          msg = String(d.message ?? d.Message ?? d.msg ?? d.Msg ?? '')
        }
      }
      if (!msg.trim() && err instanceof Error) msg = err.message
      await Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: msg.trim() || t('inventoryDiscounts.saveFailed'),
        confirmButtonText: t('common.ok'),
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  if (!isNew && detailLoading) {
    return (
      <div className="flex min-h-[320px] flex-col items-center justify-center gap-3 p-8">
        <p className="text-muted-foreground">{t('common.loading')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex gap-3">
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <FileText className="h-5 w-5" />
        </div>
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryDiscounts.formTitle')}</h1>
          <p className="text-muted-foreground text-sm">{t('inventoryDiscounts.formSubtitle')}</p>
        </div>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-lg">{t('inventoryDiscounts.sectionHeader')}</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-3">
          <div className="space-y-2">
            <Label htmlFor="discount-date">{t('inventoryDiscounts.date')}</Label>
            <Input
              id="discount-date"
              type="date"
              value={docDate}
              onChange={(e) => setDocDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount-start">{t('inventoryDiscounts.startDate')}</Label>
            <Input
              id="discount-start"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="discount-end">{t('inventoryDiscounts.endDate')}</Label>
            <Input
              id="discount-end"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <CardTitle className="text-lg">{t('inventoryDiscounts.sectionLines')}</CardTitle>
          <div className="flex flex-wrap gap-2">
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
              <Plus className="mr-2 h-4 w-4" />
              {t('inventoryDiscounts.addRow')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
              {t('inventoryDiscounts.add3Rows')}
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
              {t('inventoryDiscounts.add5Rows')}
            </Button>
          </div>
        </CardHeader>
        <CardContent className="p-0 sm:p-6 sm:pt-0">
          <div className="overflow-x-auto rounded-md border">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead className={tableHeaderClass}>{t('inventoryDiscounts.colItem')}</TableHead>
                  <TableHead className={tableHeaderClass}>{t('inventoryDiscounts.colBatch')}</TableHead>
                  <TableHead className={`${tableHeaderClass} text-center`}>
                    {t('inventoryDiscounts.colDiscountPercent')}
                  </TableHead>
                  <TableHead className={`${tableHeaderClass} w-[56px] text-center`} />
                </TableRow>
              </TableHeader>
              <TableBody>
                {lines.map((line, index) => {
                  const batchLoading = batchesLoading[line.id]
                  const batchList = batchesByLine[line.id] ?? []
                  const branchOk = Boolean(selectedBranchId)

                  return (
                    <TableRow key={line.id}>
                      <TableCell className="min-w-[200px] align-top">
                        <Select
                          value={line.itemId || SELECT_EMPTY}
                          onValueChange={(v) => handleItemChange(index, v)}
                          disabled={!branchOk || itemsLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryDiscounts.selectItem')} />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_EMPTY}>—</SelectItem>
                            {itemOptions.map((o) => (
                              <SelectItem key={`${line.id}-${o.value}`} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="min-w-[180px] align-top">
                        <Select
                          value={line.batchId || SELECT_ANY}
                          onValueChange={(v) =>
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = {
                                ...next[index],
                                batchId: v,
                              }
                              return next
                            })
                          }
                          disabled={!branchOk || !line.itemId || batchLoading}
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryDiscounts.selectBatch')} />
                          </SelectTrigger>
                          <SelectContent searchable>
                            <SelectItem value={SELECT_ANY}>
                              {t('inventoryDiscounts.batchAny', { defaultValue: 'Any' })}
                            </SelectItem>
                            {batchList.map((b) => (
                              <SelectItem key={`${line.id}-${b.id}`} value={String(b.id)}>
                                {b.code || b.reference || String(b.id)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </TableCell>
                      <TableCell className="w-[120px] align-top">
                        <Input
                          type="text"
                          inputMode="decimal"
                          autoComplete="off"
                          className="h-9"
                          value={line.discountPercent}
                          onChange={(e) => {
                            const v = sanitizeDiscountPercentRaw(e.target.value)
                            setLines((prev) => {
                              const next = [...prev]
                              next[index] = { ...next[index], discountPercent: v }
                              return next
                            })
                          }}
                        />
                      </TableCell>
                      <TableCell className="align-top text-center">
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                          onClick={() => removeLine(index)}
                          disabled={lines.length <= 1}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-end sm:gap-2">
        <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
          <ArrowLeft className="mr-2 h-4 w-4" />
          {t('inventoryDiscounts.back')}
        </Button>
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={saveLoading}
          className="w-full sm:w-auto"
        >
          <Save className="mr-2 h-4 w-4" />
          {saveLoading
            ? t('common.saving', { defaultValue: 'Saving…' })
            : !isNew
              ? t('common.update')
              : t('inventoryDiscounts.save')}
        </Button>
      </div>
    </div>
  )
}
